const THUMB_ACTIVE_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
  <path d="M7.4386 0L0 12.5526L7.4386 8.83333L14.4123 12.5526L7.4386 0Z" fill="#C4BDBD" style="fill:#C4BDBD;fill:color(display-p3 0.7686 0.7412 0.7412);fill-opacity:1;"/>
</svg>`;

const THUMB_RECENTER_SPEED = 800;

class BehindThePieces extends HTMLElement {
  constructor() {
    super();
    this.pagerSwiperEl = this.querySelector('.behind-the-pieces__pager');
    this.mainSwiperEl = this.querySelector('.behind-the-pieces__main-swiper');
    this.thumbsSwiperEl = this.querySelector('.behind-the-pieces__thumbs-swiper');
    this.enterTrigger = this.querySelector('[data-enter-trigger]');
    this.galleryTitle = this.querySelector('[data-gallery-title]');
    this.galleryCollection = this.querySelector('[data-gallery-collection]');
    this.galleryShopLink = this.querySelector('[data-gallery-shop-link]');

    // One shared dialog for every slide's lightbox trigger (see
    // blocks/behind-the-piece-item.liquid's doc comment) — the triggers
    // are read fresh in connectedCallback rather than here, since Swiper
    // hasn't unwrapped the .shopify-block wrappers around each slide yet
    // at construction time (doesn't actually affect whether the triggers
    // themselves are found, just keeps the two in the same place).
    this.lightboxDialog = this.querySelector('[data-lightbox-dialog]');
    this.lightboxImage = this.querySelector('[data-lightbox-image]');
    this.lightboxClose = this.querySelector('[data-lightbox-close]');
  }

  connectedCallback() {
    this.pagerSwiperEl?.initialize();
    this.enterTrigger?.addEventListener('click', this.onEnterClick);

    this.lightboxTriggers = this.querySelectorAll('[data-lightbox-trigger]');
    this.lightboxTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onLightboxTriggerClick);
    });
    this.lightboxClose?.addEventListener('click', this.closeLightbox);
    this.lightboxDialog?.addEventListener('click', (event) => {
      if (event.target === this.lightboxDialog) this.closeLightbox();
    });

    if (!this.mainSwiperEl || !this.thumbsSwiperEl) return;

    // Shopify wraps every block rendered via `content_for 'blocks'` in its
    // own `.shopify-block` div, but swiper-container requires its direct
    // children to be exactly <swiper-slide> elements — unwrap those divs
    // so Swiper can find the slides. block.shopify_attributes is already
    // on the <swiper-slide> itself, so editor block-selection still works.
    this.unwrapShopifyBlocks(this.mainSwiperEl);

    // The thumbnail swiper (a separate Swiper instance, per Swiper's
    // thumbs module) is built here from data attributes on the main slides.
    this.mainSwiperEl.querySelectorAll('.behind-the-pieces__main-slide').forEach((slide) => {
      const thumbSlide = document.createElement('swiper-slide');
      thumbSlide.className = 'behind-the-pieces__thumb-slide';

      if (slide.dataset.thumbnailSrc) {
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'behind-the-pieces__thumb-image';

        const img = document.createElement('img');
        img.src = slide.dataset.thumbnailSrc;
        img.alt = slide.dataset.thumbnailAlt || '';
        img.loading = 'lazy';

        imageWrapper.appendChild(img);
        thumbSlide.appendChild(imageWrapper);
      }

      const arrow = document.createElement('span');
      arrow.className = 'behind-the-pieces__thumb-arrow';
      arrow.innerHTML = THUMB_ACTIVE_ARROW_SVG;
      thumbSlide.appendChild(arrow);

      this.thumbsSwiperEl.appendChild(thumbSlide);
    });

    // `centeredSlidesBounds` is deliberately left off: it pins the first/last
    // slide to the track's edge instead of the screen's centre, which would
    // break the "active slide is always dead centre" behaviour this strip
    // relies on. Without it, Swiper's own centering already puts equal empty
    // space on either side of the first/last slide, so no manual
    // slidesOffsetBefore/After is needed either.
    Object.assign(this.thumbsSwiperEl, {
      speed: 800,
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 24,
      watchSlidesProgress: true,
      grabCursor: true
    });
    this.thumbsSwiperEl.initialize();

    Object.assign(this.mainSwiperEl, {
      thumbs: { swiper: this.thumbsSwiperEl.swiper },
      breakpoints: {
        1024: {
          speed: 0,
        }
      },
    });
    this.mainSwiperEl.initialize();

    // Swiper's thumbs module only toggles the active thumb class when the
    // thumbs swiper has freeMode enabled — it won't re-scroll the strip, on
    // the assumption that a free-scrolling thumb strip shouldn't be forced
    // to move under the user. This strip is meant to always keep the active
    // thumb centred, so drive that ourselves on every main-swiper change.
    this.mainSwiperEl.swiper.on('slideChange', this.recenterActiveThumb);
    this.mainSwiperEl.swiper.on('slideChange', this.updateGalleryInfo);
    this.updateGalleryInfo();
  }

  recenterActiveThumb = () => {
    this.thumbsSwiperEl.swiper?.slideTo(this.mainSwiperEl.swiper.activeIndex, THUMB_RECENTER_SPEED);
  };

  unwrapShopifyBlocks(container) {
    container.querySelectorAll(':scope > .shopify-block').forEach((wrapper) => {
      while (wrapper.firstChild) {
        wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
      }
      wrapper.remove();
    });
  }

  disconnectedCallback() {
    this.enterTrigger?.removeEventListener('click', this.onEnterClick);
    this.lightboxTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.onLightboxTriggerClick);
    });
    this.lightboxClose?.removeEventListener('click', this.closeLightbox);
  }

  onEnterClick = () => {
    this.pagerSwiperEl?.swiper?.slideNext();
  };

  onLightboxTriggerClick = (event) => {
    if (!this.lightboxDialog || !this.lightboxImage) return;

    this.lightboxImage.src = event.currentTarget.dataset.lightboxSrc || '';
    this.lightboxImage.alt = event.currentTarget.dataset.lightboxAlt || '';

    if (!this.lightboxDialog.open) this.lightboxDialog.showModal();
  };

  closeLightbox = () => {
    if (!this.lightboxDialog?.open) return;

    this.lightboxDialog.classList.add('behind-the-pieces__lightbox--closing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.lightboxDialog.classList.remove('behind-the-pieces__lightbox--closing');
      this.lightboxDialog.close();
    };
    this.lightboxDialog.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 400);
  };

  updateGalleryInfo = () => {
    const swiper = this.mainSwiperEl?.swiper;
    const activeSlide = swiper?.slides[swiper.activeIndex];
    if (!activeSlide) return;

    if (this.galleryTitle) {
      this.galleryTitle.textContent = activeSlide.dataset.title || '';
    }

    const collectionTitle = activeSlide.dataset.collectionTitle || '';
    const collectionUrl = activeSlide.dataset.collectionUrl || '';

    if (this.galleryCollection) {
      this.galleryCollection.textContent = collectionTitle;
      this.galleryCollection.href = collectionUrl;
      this.galleryCollection.hidden = collectionTitle === '';
    }

    if (this.galleryShopLink) {
      this.galleryShopLink.href = collectionUrl;
      this.galleryShopLink.hidden = collectionUrl === '';
    }
  };
}

customElements.define('behind-the-pieces', BehindThePieces);
